import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ExpandableTextProps {
	text: string;
	// Lines shown before the rest is hidden behind "Show More".
	maxLines?: number;
	// Typography styles for the text itself (font size, colour, ...).
	sx?: SxProps<Theme>;
}

// Multi-line text that clamps to `maxLines` and offers a Show More / Show Less
// toggle, but only when the text is actually clipped. Overflow is re-measured
// on mount, on text change, and whenever the element resizes via a
// ResizeObserver.
const ExpandableText: FC<ExpandableTextProps> = ({
	text,
	maxLines = 2,
	sx,
}) => {
	const { t } = useTranslation();
	const textRef = useRef<HTMLSpanElement | null>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isOverflowed, setIsOverflowed] = useState(false);

	useEffect(() => {
		setIsExpanded(false);
	}, [text]);

	useEffect(() => {
		const el = textRef.current;
		// Only a clamped element can tell whether it overflows; while expanded
		// keep the last answer so the Show Less toggle stays put.
		if (!el || isExpanded) {
			return;
		}
		const checkOverflow = () => {
			setIsOverflowed(el.scrollHeight > el.clientHeight + 1);
		};
		checkOverflow();
		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(el);
		return () => resizeObserver.disconnect();
	}, [text, maxLines, isExpanded]);

	const handleToggle = useCallback(() => {
		setIsExpanded((previous) => !previous);
	}, []);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
			<Typography
				ref={textRef}
				sx={[
					{
						wordBreak: "break-word",
						whiteSpace: "pre-line",
					},
					!isExpanded && {
						display: "-webkit-box",
						WebkitLineClamp: maxLines,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					},
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			>
				{text}
			</Typography>
			{isOverflowed ? (
				<Button
					size="small"
					onClick={handleToggle}
					aria-expanded={isExpanded}
					sx={{
						alignSelf: "flex-start",
						minWidth: 0,
						padding: 0,
					}}
				>
					{isExpanded ? t("scheduler_show-less") : t("scheduler_show-more")}
				</Button>
			) : null}
		</Box>
	);
};

export default ExpandableText;
